import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { resolveSQDCWorkflowParallelJoinTarget, type SQDCWorkflowDefinition } from "./domain";
import { useSQDCWorkflowDefinitions, useSQDCWorkflowInstances } from "./useSQDCWorkflows";

export function useSQDCWorkflowRuntime(
  workflowInstanceId: MaybeRefOrGetter<string | undefined>,
  workflowStepId: MaybeRefOrGetter<string | undefined>,
  workflowDefinitionId?: MaybeRefOrGetter<string | undefined>,
  workflowDefinition?: MaybeRefOrGetter<SQDCWorkflowDefinition | undefined>,
) {
  const hasWorkflowContext = computed(
    () =>
      toValue(workflowInstanceId) != null ||
      toValue(workflowDefinitionId) != null ||
      toValue(workflowDefinition) != null,
  );
  const { instances, refetch: refetchInstances } = useSQDCWorkflowInstances(
    computed(() => ({
      ids: toValue(workflowInstanceId) == null ? [] : [toValue(workflowInstanceId)!],
    })),
  );
  const instance = computed(() => {
    const instanceId = toValue(workflowInstanceId);
    if (instanceId == null) return undefined;
    return instances.value.find((candidate) => candidate.workflowInstanceId === instanceId);
  });
  const { definitions } = useSQDCWorkflowDefinitions(
    computed(() => ({
      ids:
        !hasWorkflowContext.value ||
        (instance.value == null && toValue(workflowDefinitionId) == null)
          ? []
          : [instance.value?.definitionId ?? toValue(workflowDefinitionId)!],
    })),
  );
  const definition = computed(() => {
    if (!hasWorkflowContext.value) return undefined;
    const providedDefinition = toValue(workflowDefinition);
    if (providedDefinition != null) return providedDefinition;
    const definitionId = instance.value?.definitionId ?? toValue(workflowDefinitionId);
    return definitions.value.find((candidate) => candidate.id === definitionId);
  });
  const step = computed(() => {
    const instanceStep = instance.value?.steps.find(
      (candidate) => candidate.workflowStepId === toValue(workflowStepId),
    );
    if (instanceStep != null) return instanceStep;
    if (toValue(workflowInstanceId) != null) return undefined;

    const startNode = definition.value?.nodes.find((node) => node.type === "START");
    const firstNodeId = definition.value?.connections.find(
      (connection) => connection.sourceNodeId === startNode?.id,
    )?.targetNodeId;
    const firstNode = definition.value?.nodes.find((node) => node.id === firstNodeId);
    if (firstNode == null) return undefined;
    return {
      workflowStepId: "",
      node: firstNode,
      createdAt: "",
    };
  });
  const transitions = computed(() => {
    const currentDefinition = definition.value;
    const currentStep = step.value;
    if (currentDefinition == null || currentStep == null) return [];
    const definitionNode = currentDefinition.nodes.find(({ id }) => id === currentStep.node.id);
    const parallelBranchId =
      "parallelBranchId" in currentStep ? currentStep.parallelBranchId : undefined;
    const outgoingTransitions = currentDefinition.connections
      .filter((connection) => connection.sourceNodeId === currentStep.node.id)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((connection) => {
        const directTargetNode = currentDefinition.nodes.find(
          (node) => node.id === connection.targetNodeId,
        );
        const completesParallelBoundary =
          parallelBranchId != null &&
          directTargetNode?.type === "PARALLEL" &&
          directTargetNode.id === definitionNode?.parentParallelNodeId;
        const readyTarget = completesParallelBoundary
          ? resolveSQDCWorkflowParallelJoinTarget(
              instance.value!,
              currentDefinition,
              parallelBranchId,
            )
          : undefined;
        const targetNode = readyTarget ?? directTargetNode;
        return {
          ...connection,
          targetNode,
          label: "",
          completesParallelBranch: completesParallelBoundary,
          waitsForParallelJoin: completesParallelBoundary && readyTarget == null,
        };
      });
    return outgoingTransitions;
  });

  return {
    instance,
    definition,
    step,
    transitions,
    refetchInstances,
  };
}
